import {
  GamoraBase64URLGuard,
  GamoraErrorUnhandledException,
  assert,
} from '@ver.id/libs.internal.gamora';
import { HoruzProtocolChulakDataType } from '../../data.js';
import { HoruzProtocolChulakMessageGuard } from './base.js';

/**
 * Input type
 * @public
 */
export interface HoruzProtocolChulakMessageAppPresentationCompletedGuardInput {
  compact: string;
}

/**
 * @public
 */
export class HoruzProtocolChulakMessageAppPresentationCompletedGuard extends HoruzProtocolChulakMessageGuard<
  HoruzProtocolChulakDataType.DATA_APP_PRESENTATION_COMPLETED,
  HoruzProtocolChulakMessageAppPresentationCompletedGuardInput
> {
  /**
   * Valid Compact Regex
   */
  static CompactRegExp =
    /^(?<header>[a-zA-Z0-9\-_]+)\.(?<payload>[a-zA-Z0-9\-_]+)\.(?<signature>[a-zA-Z0-9\-_]+)$/;
  private _compact = '';

  /**
   * Constructor
   * @param input - the input
   */
  constructor(
    input: HoruzProtocolChulakMessageAppPresentationCompletedGuardInput,
  ) {
    super();
    this._compact = input.compact;
  }

  /**
   * Gets type
   */
  get type(): HoruzProtocolChulakDataType.DATA_APP_PRESENTATION_COMPLETED {
    return HoruzProtocolChulakDataType.DATA_APP_PRESENTATION_COMPLETED;
  }

  /**
   * AuthenticationCompact
   */
  get compact() {
    return this._compact;
  }
  set compact(compact: string) {
    assert(
      HoruzProtocolChulakMessageAppPresentationCompletedGuard.CompactRegExp.test(
        compact,
      ),
      'Invalid compact: not a valid json web signature string.',
    );
    this._compact = compact;
  }

  /**
   * Gets from JSON
   * @param input - Input string
   */
  static override fromJSON(
    input: unknown,
  ): HoruzProtocolChulakMessageAppPresentationCompletedGuard {
    assert(
      typeof input === 'object' && input !== null,
      'Invalid input argument: not an object.',
    );
    return new HoruzProtocolChulakMessageAppPresentationCompletedGuard(
      input as HoruzProtocolChulakMessageAppPresentationCompletedGuardInput,
    );
  }

  /**
   * Get from base64URL
   * @param input - The input
   */
  static override fromBase64URL(
    input: unknown,
  ): HoruzProtocolChulakMessageAppPresentationCompletedGuard {
    assert(
      typeof input === 'string' && input.length > 0,
      'Invalid input argument: not a valid string.',
    );

    // Parse input
    let json;
    try {
      json = JSON.parse(GamoraBase64URLGuard.fromBase64URL(input).toUTF8());
    } catch {
      throw new GamoraErrorUnhandledException(
        'Unable to parse input: not valid JSON in base64url input.',
      );
    }

    // Create new guard
    return new HoruzProtocolChulakMessageAppPresentationCompletedGuard(
      json as HoruzProtocolChulakMessageAppPresentationCompletedGuardInput,
    );
  }

  /**
   * Gets JSON
   * @returns string
   */
  override toJSON() {
    return {
      compact: this._compact,
    };
  }

  /**
   * To Base64URL
   */
  override toBase64URL(): string {
    const json = this.toJSON();
    return GamoraBase64URLGuard.fromUTF8(JSON.stringify(json)).toBase64URL();
  }

  /**
   * Create clone
   */
  clone() {
    return HoruzProtocolChulakMessageAppPresentationCompletedGuard.fromJSON(
      this.toJSON(),
    );
  }
}
